import { formatQuantity } from '../../utils/inventoryLabels';

/*
 * Reading a scanned code against what the screen already knows, the way the
 * server reads it: product codes (barcode, SKU, a pack's barcode) without
 * case, accents or spaces, and a number of a GTIN's length without its leading
 * zeros — the camera reads a 12-digit UPC as 13 digits; slot codes without
 * case. Matching on the phone first is what lets a step say "ese no es"
 * before anything is sent.
 */

const GTIN_LENGTHS = [8, 12, 13, 14];

/** @param {unknown} code */
export const normalizeCode = (code) => {
  const value = String(code ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/\s+/g, '')
    .toLowerCase();
  return /^\d+$/.test(value) && GTIN_LENGTHS.includes(value.length) ? value.replace(/^0+(?=\d)/, '') : value;
};

/**
 * What a code is among a line's or a row's codes, and in which unit: the unit
 * itself (barcode or SKU), or one of its packs, with how many units it holds.
 *
 * @param {{barcode?: string, sku?: string, packs?: Array<{barcode: string, factor: number}>}|null} codes
 * @param {string} code
 * @returns {{factor: number, pack: boolean}|null}
 */
export const matchCodes = (codes, code) => {
  const wanted = normalizeCode(code);
  if (!wanted || !codes) return null;

  if ([codes.barcode, codes.sku].some((value) => value && normalizeCode(value) === wanted)) {
    return { factor: 1, pack: false };
  }
  const pack = (codes.packs || []).find((item) => item.barcode && normalizeCode(item.barcode) === wanted);
  return pack ? { factor: Number(pack.factor) || 1, pack: true } : null;
};

/**
 * The code a unit of a product is read by: its barcode, else its SKU. What a
 * tap on the product sends, so it reads as scanning one unit would.
 *
 * @param {{barcode?: string, sku?: string}|null} codes
 * @param {string} [sku]
 * @returns {string|null}
 */
export const unitCode = (codes, sku) => codes?.barcode || codes?.sku || sku || null;

/** Two slot codes are the same slot when they match without case. */
export const sameSlotCode = (a, b) =>
  Boolean(a) && Boolean(b) && String(a).trim().toLowerCase() === String(b).trim().toLowerCase();

/**
 * What a count in the unit of a code has to reach to make up `units`: the
 * units themselves, or how many boxes hold them; `null` when whole boxes
 * cannot make them up.
 *
 * @param {number} units
 * @param {number} [factor=1] - Units in the counted code.
 * @returns {number|null}
 */
export const countTarget = (units, factor = 1) => {
  const amount = Number(units) || 0;
  const per = Number(factor) || 1;
  if (per === 1) return amount;
  return Number.isInteger(amount / per) ? amount / per : null;
};

/**
 * How a quantity in the scanned unit reads, e.g. "2 cajas (24 un)" or
 * "1.000 un".
 *
 * @param {number} quantity - In the unit of the code.
 * @param {number} factor
 */
export const unitsLabel = (quantity, factor) => {
  const amount = Number(quantity) || 0;
  if (!(factor > 1)) return `${formatQuantity(amount)} un`;
  return `${formatQuantity(amount)} ${amount === 1 ? 'caja' : 'cajas'} (${formatQuantity(amount * factor)} un)`;
};
